import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Info, X, Share2 } from 'lucide-react';
import { useEffect, useState, memo, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ShareMapModal from './ShareMapModal';
import { renderToStaticMarkup } from 'react-dom/server';
import { FaExchangeAlt, FaMapMarkerAlt, FaPlane, FaRoute, FaTaxi, FaTrain } from 'react-icons/fa';
import { MdFlag } from 'react-icons/md';
import { MapContainer, Marker, Polyline, Popup, TileLayer, Tooltip, useMap } from 'react-leaflet';
import './RouteMapModal.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Helper Functions Outside Component (Static)
const formatTime = (val) => {
    if (!val) return null;
    if (val.toString().includes('T')) {
        try {
            const d = new Date(val);
            return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (e) { return val; }
    }
    return val;
};

const calculateLayover = (arr, dep) => {
    if (!arr || !dep) return null;
    try {
        const getMin = (t) => {
            if (t.includes('T')) return new Date(t).getTime() / 60000;
            const [h, m] = t.split(':').map(Number);
            return h * 60 + m;
        };
        let diff = getMin(dep) - getMin(arr);
        if (diff <= 0) diff += 24 * 60; // Handle next day departure
        return diff < 60 ? `${diff}m` : `${Math.floor(diff / 60)}h ${diff % 60}m`;
    } catch (e) { return null; }
};

const genKey = (lat, lng) => `${Number(lat).toFixed(4)},${Number(lng).toFixed(4)}`;

// Custom Icons Cache
const customIconCache = {};

const createCustomIcon = (type) => {
    if (customIconCache[type]) return customIconCache[type];

    // Google Maps Style Start Circle (Center Anchored)
    if (type === 'start') {
        const iconHtml = renderToStaticMarkup(
            <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: 'white',
                borderRadius: '50%',
                border: '3px solid #1e293b',
                boxShadow: '0 1px 3px rgba(0,0,0,0.4)'
            }} />
        );
        const leafletIcon = L.divIcon({
            html: iconHtml,
            className: 'custom-map-icon-start',
            iconSize: [16, 16],
            iconAnchor: [8, 8]
        });
        customIconCache[type] = leafletIcon;
        return leafletIcon;
    }

    // Google Maps Style End Pin (Tip Anchored)
    if (type === 'end') {
        const iconHtml = renderToStaticMarkup(
            <div style={{ color: '#EA4335', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))' }}>
                <FaMapMarkerAlt size={32} />
            </div>
        );
        const leafletIcon = L.divIcon({
            html: iconHtml,
            className: 'custom-map-icon-end',
            iconSize: [32, 32],
            iconAnchor: [16, 32] // Anchored at the bottom tip
        });
        customIconCache[type] = leafletIcon;
        return leafletIcon;
    }

    let icon;
    let color = '#475569';
    let size = '12px';
    const isMainPoint = type === 'stopover';

    if (type === 'flight') {
        icon = <FaPlane />;
        color = '#6366F1';
    } else if (type === 'train') {
        icon = <FaTrain />;
        color = '#ff5b15';
    } else if (type === 'cab' || type === 'transfer') {
        icon = <FaTaxi />;
        color = '#10B981';
    } else if (type === 'stopover') {
        icon = <FaExchangeAlt />;
        color = '#8b5cf6';
        size = '16px';
    } else {
        icon = <FaMapMarkerAlt />;
    }

    const containerSize = isMainPoint ? '30px' : '20px';
    const iconHtml = renderToStaticMarkup(
        <div
            className={`custom-icon-inner ${type === 'stopover' ? 'stopover-glow' : ''}`}
            style={{
                color: color,
                fontSize: size,
                backgroundColor: 'white',
                borderRadius: '50%',
                width: containerSize,
                height: containerSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                border: `${isMainPoint ? '2.5px' : '1.5px'} solid ${color}`,
                transition: 'all 0.3s ease'
            }}
        >
            {icon}
        </div>
    );

    const LSize = isMainPoint ? 32 : 22;
    const leafletIcon = L.divIcon({
        html: iconHtml,
        className: 'custom-map-icon',
        iconSize: [LSize, LSize],
        iconAnchor: [LSize / 2, LSize / 2]
    });

    customIconCache[type] = leafletIcon;
    return leafletIcon;
};

const FitBounds = ({ points }) => {
    const map = useMap();
    useEffect(() => {
        if (points && points.length > 0 && map) {
            try {
                const bounds = L.latLngBounds(points);
                if (map.getContainer()) {
                    map.flyToBounds(bounds, {
                        padding: [50, 50],
                        duration: 0.5,
                        easeLinearity: 1.25,
                        maxZoom: 13
                    });
                }
            } catch (err) {
                console.warn("Map fitBounds failed:", err);
            }
        }
    }, [points, map]);
    return null;
};

const RouteMapModal = ({ isOpen, onClose, routeData, title, searchMetadata }) => {
    const [mapData, setMapData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    console.log(routeData)
    useEffect(() => {
        const fetchMapData = async () => {
            if (!routeData) return;
            setLoading(true);
            setError(null);
            try {
                const payload = {
                    route_obj: {
                        status: 200,
                        data: {
                            routes: [routeData],
                            ...(searchMetadata || {})
                        }
                    }
                };
                const response = await fetch('https://hh183pxn0f.execute-api.ap-south-1.amazonaws.com/development/maps', {
                    method: 'POST',
                    priority: 'high',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    const errorJson = await response.json().catch(() => ({}));
                    throw new Error(errorJson.body?.error || errorJson.error || `Error ${response.status}: Failed to fetch map data`);
                }
                const data = await response.json();
                if (Array.isArray(data) && data.length > 0) setMapData(data[0]);
                else if (data && data.segments) setMapData(data);
                else throw new Error('Invalid map data received from server');
            } catch (err) {
                console.error('RouteMapModal API Error:', err);
                setError(err.message);
                setMapData(null);
            } finally {
                setLoading(false);
            }
        };

        if (isOpen) fetchMapData();
        else { setMapData(null); setError(null); }
    }, [isOpen, routeData, retryCount]);

    const handleRetry = () => setRetryCount(prev => prev + 1);

    // 1. Memoized Header Title
    const headerTitleInfo = useMemo(() => {
        if (!mapData?.segments) return title || 'Journey Route Map';
        const startNode = mapData.segments[0].nodes[0];
        const lastSegNodes = mapData.segments[mapData.segments.length - 1].nodes;
        const endNode = lastSegNodes[lastSegNodes.length - 1];

        // Prioritize search intent names from metadata if available for a more personalized title
        const startCityText = searchMetadata?.user_src_name || `${startNode.name}${startNode.code ? ` (${startNode.code})` : ''}`;
        const endCityText = searchMetadata?.user_dest_name || `${endNode.name}${endNode.code ? ` (${endNode.code})` : ''}`;

        const viaCities = mapData.segments.slice(0, -1).map(seg => seg.nodes[seg.nodes.length - 1].name);
        const uniqueVia = [...new Set(viaCities)].slice(0, 2);
        const viaText = uniqueVia.length > 0 ? ` (Via ${uniqueVia.join(', ')})` : '';

        return (
            <>
                {startCityText} to {endCityText}
                {viaText && <span className="title-via">{viaText}</span>}
            </>
        );
    }, [mapData, title]);

    // 2. Memoized Duration Chip
    const durationChip = useMemo(() => {
        if (!routeData) return null;
        const rawDuration = routeData.duration || routeData.duration_text || routeData.total_duration || routeData.totalDuration || routeData.travel_time;

        const calculateExactDuration = () => {
            const nodes = routeData.nodes || [];
            if (nodes.length === 0) return null;
            let totalMins = 0;
            let hasValidData = true;

            nodes.forEach((node, index) => {
                const seg = node.segment || {};
                const dur = parseInt(seg.duration_mins);
                if (!isNaN(dur)) totalMins += dur;
                else hasValidData = false;

                if (index < nodes.length - 1) {
                    const nextSeg = nodes[index + 1].segment || {};
                    const arrivalTime = new Date(seg.dest_arr_dt);
                    const departureTime = new Date(nextSeg.origin_dep_dt);
                    if (!isNaN(arrivalTime.getTime()) && !isNaN(departureTime.getTime())) {
                        const diffMins = Math.floor((departureTime - arrivalTime) / (1000 * 60));
                        if (diffMins > 0) totalMins += diffMins;
                    } else hasValidData = false;
                }
            });

            if (hasValidData && totalMins > 0) return `${Math.floor(totalMins / 60)}h ${totalMins % 60}m`;
            try {
                const firstSeg = nodes[0].segment;
                const lastSeg = nodes[nodes.length - 1].segment;
                const diffTime = new Date(lastSeg.dest_arr_dt) - new Date(firstSeg.origin_dep_dt);
                if (!isNaN(diffTime) && diffTime > 0) return `${Math.floor(diffTime / (1000 * 60 * 60))}h ${Math.floor((diffTime / (1000 * 60)) % 60)}m`;
            } catch (e) { }
            return null;
        };

        const displayDuration = rawDuration || calculateExactDuration();
        if (displayDuration) return (
            <div className="duration-chip" style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: '#fff7ed', color: '#c2410c', padding: '3px 10px',
                borderRadius: '50px', fontSize: '0.72rem', fontWeight: 700,
                border: '1px solid #ffedd5', marginTop: '4px', alignSelf: 'flex-start'
            }}>
                <span style={{ opacity: 0.7, fontWeight: 600 }}>Total Duration:</span>
                <span>{displayDuration}</span>
            </div>
        );
        return null;
    }, [routeData]);

    // 3. Memoized Coordinate Points for FitBounds
    const allPoints = useMemo(() => {
        const points = [];
        if (mapData?.segments) {
            mapData.segments.forEach(seg => {
                seg.nodes.forEach(node => {
                    if (node.lat !== undefined && node.lng !== undefined && !(node.lat === 0 && node.lng === 0)) {
                        points.push([node.lat, node.lng]);
                    }
                });
            });
        }
        return points;
    }, [mapData]);

    // 4. Memoized Core Map Elements (Polylines, Markers, Labels)
    const mapElements = useMemo(() => {
        if (!mapData?.segments) return null;

        const colors = {
            train: ['#ff5b15', '#fb923c'],
            flight: ['#6366F1', '#a5b4fc'],
            cab: ['#10B981', '#6ee7b7'],
            transfer: ['#10B981', '#6ee7b7']
        };

        const modeCounters = { train: 0, flight: 0, cab: 0, transfer: 0 };
        const validSegments = mapData.segments.filter(seg => {
            const lastNode = seg.nodes[seg.nodes.length - 1];
            return !(lastNode?.lat === 0 && lastNode?.lng === 0);
        });

        if (validSegments.length === 0) return null;

        // Build intermediate junctions map once
        const markersMap = new Map();
        const startKey = genKey(validSegments[0].nodes[0].lat, validSegments[0].nodes[0].lng);
        const lastSegNodes = validSegments[validSegments.length - 1].nodes;
        const endKey = genKey(lastSegNodes[lastSegNodes.length - 1].lat, lastSegNodes[lastSegNodes.length - 1].lng);

        validSegments.forEach((seg) => {
            seg.nodes.forEach((n, nIdx) => {
                if (!n || (n.lat === 0 && n.lng === 0)) return;
                const key = genKey(n.lat, n.lng);
                if (key === startKey || key === endKey) return;

                if (!markersMap.has(key)) {
                    markersMap.set(key, { pos: [n.lat, n.lng], node: { ...n }, type: seg.type, isStopover: false, inbound: null, outbound: null });
                }
                const current = markersMap.get(key);
                
                // Helper to find time in original routeData if map response is missing it
                const findSocketTime = (name, type) => {
                    if (!routeData?.nodes) return null;
                    const norm = (s) => s?.toLowerCase().replace(/[^a-z0-9]/g, '').trim();
                    const targetNorm = norm(name);
                    
                    for (const rn of routeData.nodes) {
                        const s = rn.segment;
                        if (type === 'arr' && (norm(s?.dest_name) === targetNorm || norm(s?.dest_code) === targetNorm)) 
                            return s.dest_arr_dt || s.dest_arr;
                        if (type === 'dep' && (norm(s?.origin_name) === targetNorm || norm(s?.origin_code) === targetNorm)) 
                            return s.origin_dep_dt || s.origin_dep;
                    }
                    return null;
                };

                if (nIdx === seg.nodes.length - 1) { // End of segment
                    current.node.arr = n.arr || seg.dest_arr_dt || seg.dest_arr || findSocketTime(n.name, 'arr');
                    current.inbound = seg;
                    current.isStopover = true;
                    current.type = 'stopover';
                }
                if (nIdx === 0) { // Start of segment
                    current.node.dep = n.dep || seg.origin_dep_dt || seg.origin_dep || findSocketTime(n.name, 'dep');
                    current.outbound = seg;
                    if (current.inbound) { current.isStopover = true; current.type = 'stopover'; }
                }
            });
        });

        return (
            <>
                {/* Polylines */}
                {validSegments.map((seg, sIdx) => {
                    const positions = seg.nodes.filter(n => !(n.lat === 0 && n.lng === 0)).map(n => [n.lat, n.lng]);
                    const palette = colors[seg.type] || ['#475569', '#94a3b8'];
                    const color = palette[modeCounters[seg.type] % palette.length];
                    modeCounters[seg.type]++;
                    return (
                        <Polyline key={`poly-${sIdx}`} positions={positions} pathOptions={{ color: color, weight: 4, opacity: 0.8 }}>
                            <Popup>
                                <div className="popup-content">
                                    <strong>{seg.type.toUpperCase()}</strong><br />
                                    {seg.id && <span>ID: {seg.id}<br /></span>}
                                    {seg.from_code} ➔ {seg.to_code}
                                </div>
                            </Popup>
                        </Polyline>
                    );
                })}

                {/* Segment Labels */}
                {validSegments.map((seg, sIdx) => {
                    const vNodes = seg.nodes.filter(n => !(n.lat === 0 && n.lng === 0));
                    if (vNodes.length < 2) return null;
                    const vL = vNodes.length;
                    const lp = vL === 2 ? [(vNodes[0].lat + vNodes[1].lat) / 2, (vNodes[0].lng + vNodes[1].lng) / 2] : [vNodes[Math.floor(vL / 2)].lat, vNodes[Math.floor(vL / 2)].lng];

                    const emoji = seg.type === 'flight' ? '✈️' : seg.type === 'train' ? '🚂' : '🚕';
                    const displayType = seg.type.charAt(0).toUpperCase() + seg.type.slice(1);
                    const dispId = (seg.id && !(seg.id.toLowerCase() === seg.type.toLowerCase() || seg.id.toLowerCase() === 'cab')) ? `#${seg.id}` : '';
                    const distKm = seg.meta?.distance_kms || seg.distance_kms;
                    const timeMins = seg.meta?.time_mins || seg.time_mins;

                    const phtml = renderToStaticMarkup(<span className={`route-pill-label underlined ${seg.type}`}>{emoji} {displayType} {dispId}</span>);

                    return (
                        <Marker key={`label-${sIdx}`} position={lp} icon={L.divIcon({ html: phtml, className: `route-pill-icon ${seg.type}`, iconSize: [null, null], iconAnchor: [40, 28] })}>
                            <Tooltip direction="top" offset={[0, -15]} className="route-hover-tooltip">
                                <div className={`modern-map-tooltip ${seg.type}`}>
                                    <div className="tooltip-header" style={{ background: seg.type === 'train' ? '#ff5b15' : seg.type === 'flight' ? '#6366F1' : '#10B981' }}>
                                        <strong>{dispId || displayType}</strong>
                                    </div>
                                    <div className="tooltip-body">
                                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
                                            {vNodes[0]?.name || seg.from_code} ➔ {vNodes[vNodes.length - 1]?.name || seg.to_code}
                                        </div>
                                        {(distKm || timeMins) && (
                                            <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {emoji} {distKm ? `${distKm} km` : ''}{distKm && timeMins ? ' · ' : ''}{timeMins ? `${timeMins} min` : ''}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Tooltip>
                        </Marker>
                    );
                })}

                {/* Intermediate Station Tooltips */}
                {Array.from(markersMap.values())
                    .filter(m => m.isStopover || /\bJN\b|\bjn\b|\bJunction\b|\bjunction\b/i.test(m.node.name))
                    .map((m, i) => {
                        const arrT = formatTime(m.node.arr);
                        const depT = formatTime(m.node.dep);
                        const layo = calculateLayover(m.node.arr || '', m.node.dep || '');
                        return (
                            <Marker key={i} position={m.pos} icon={createCustomIcon(m.type)}>
                                <Tooltip direction="top" offset={[0, -5]} opacity={1}>
                                    <div className={`modern-map-tooltip ${m.isStopover ? 'stopover' : 'junction'}`}>
                                        <div className="tooltip-header">
                                            <strong>{m.node.name}</strong>
                                            {m.isStopover && <span className="stopover-badge">Stopover</span>}
                                        </div>
                                        <div className="tooltip-body">
                                            <div className="timing-container">
                                                <div className="timing-col">
                                                    <span className="label">Arrival {m.inbound?.id && <span className="train-id">(#{m.inbound.id})</span>}</span>
                                                    <span className="time">{arrT || '--:--'}</span>
                                                </div>
                                                <div className="v-divider"></div>
                                                <div className="timing-col">
                                                    <span className="label">Departure {m.outbound?.id && <span className="train-id">(#{m.outbound.id})</span>}</span>
                                                    <span className="time">{depT || '--:--'}</span>
                                                </div>
                                            </div>
                                            {m.isStopover && layo && (
                                                <>
                                                    <div className="h-divider"></div>
                                                    <div className="gap-row">Gap: {layo}</div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </Tooltip>
                            </Marker>
                        );
                    })}

                {/* Explicit Start and End Pins */}
                {(() => {
                    const startNode = validSegments[0].nodes[0];
                    const lastSeg = validSegments[validSegments.length - 1];
                    const endNode = lastSeg.nodes[lastSeg.nodes.length - 1];
                    return (
                        <>
                            <Marker position={[startNode.lat, startNode.lng]} icon={createCustomIcon('start')} zIndexOffset={1000}>
                                <Tooltip direction="top" offset={[0, -5]} opacity={1}>
                                    <div className="modern-map-tooltip junction">
                                        <div className="tooltip-header">
                                            <strong>{startNode.name}</strong>
                                        </div>
                                        <div className="tooltip-body">
                                            <div className="simple-time-row">
                                                <span className="label">Departure:</span>
                                                <span className="time">{formatTime(startNode.dep || startNode.origin_dep || startNode.origin_dep_dt) || '--:--'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Tooltip>
                            </Marker>
                            <Marker position={[startNode.lat, startNode.lng]} icon={L.divIcon({ html: `<div class="station-name-pill source">${startNode.name}</div>`, className: 'floating-city-label', iconSize: [null, null], iconAnchor: [0, 0] })} interactive={false} />

                            <Marker position={[endNode.lat, endNode.lng]} icon={createCustomIcon('end')} zIndexOffset={1000}>
                                <Tooltip direction="top" offset={[0, -5]} opacity={1}>
                                    <div className="modern-map-tooltip junction">
                                        <div className="tooltip-header">
                                            <strong>{endNode.name}</strong>
                                        </div>
                                        <div className="tooltip-body">
                                            <div className="simple-time-row">
                                                <span className="label">Arrival:</span>
                                                <span className="time">
                                                    {formatTime(
                                                        endNode.arr || 
                                                        endNode.dest_arr || 
                                                        endNode.dest_arr_dt || 
                                                        (routeData.nodes?.[routeData.nodes.length - 1]?.segment?.dest_arr_dt) ||
                                                        (routeData.nodes?.[routeData.nodes.length - 1]?.segment?.dest_arr)
                                                    ) || '--:--'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Tooltip>
                            </Marker>
                            <Marker position={[endNode.lat, endNode.lng]} icon={L.divIcon({ html: `<div class="station-name-pill dest">${endNode.name}</div>`, className: 'floating-city-label', iconSize: [null, null], iconAnchor: [0, 0] })} interactive={false} />
                        </>
                    );
                })()}

                {/* Transfer Polylines */}
                {mapData.stops?.map((stop, stIdx) => (
                    <Polyline key={`stop-${stIdx}`} positions={[[stop.from_node.lat, stop.from_node.lng], [stop.to_node.lat, stop.to_node.lng]]} pathOptions={{ color: '#10B981', weight: 3, opacity: 0.6 }}>
                        <Popup><div className="popup-content"><strong>Transfer ({stop.mode})</strong><br />{stop.commute_dist_kms} km, {stop.commute_time_mins} mins</div></Popup>
                        <Tooltip permanent direction="top" offset={[0, -10]} className="cab-tooltip"><span>🚕 Cab {stop.commute_dist_kms ? `· ${stop.commute_dist_kms} km` : ''}{stop.commute_time_mins ? ` · ${stop.commute_time_mins} min` : ''}</span></Tooltip>
                    </Polyline>
                ))}
            </>
        );
    }, [mapData]);

    if (!isOpen) return null;

    return createPortal(
        <div className="route-map-modal-overlay" onClick={onClose}>
            <div className="route-map-modal-content" onClick={e => e.stopPropagation()}>
                <div className="route-map-modal-header">
                    <div className="header-info">
                        <div className="modal-title-container">
                            <div className="route-icon-box"><FaRoute size={28} /></div>
                            <div className="title-group" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <h3>{headerTitleInfo}</h3>
                                {durationChip}
                            </div>
                        </div>
                    </div>
                    <div className="header-actions" style={{ display: 'flex', gap: '8px' }}>
                        <button className="share-trigger-btn" onClick={() => setIsShareModalOpen(true)} title="Share Route">
                            <Share2 size={20} />
                        </button>
                        <button className="close-btn" onClick={onClose}><X size={24} /></button>
                    </div>
                </div>

                <div className="route-map-modal-body">
                    {loading ? (
                        <div className="map-loading"><div className="spinner"></div><p>Plotting coordinates...</p></div>
                    ) : mapData ? (
                        <div className="map-wrapper">
                            <MapContainer
                                key={mapData.route_id || 'initial'}
                                center={allPoints.length > 0 ? allPoints[0] : [23.178, 80.052]}
                                zoom={6}
                                style={{ height: '100%', width: '100%' }}
                                zoomControl={false}
                                zoomSnap={0}
                                zoomDelta={0.2}
                                wheelPxPerZoomLevel={30}
                                animate={true}
                                wheelDebounceTime={30}
                            >
                                <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>' />
                                {mapElements}
                                <FitBounds points={allPoints} />
                            </MapContainer>

                            <div className="map-legend">
                                {mapData.segments.some(s => s.type === 'flight') && <div className="legend-item"><span className="line flight"></span><span>Flight</span></div>}
                                {mapData.segments.some(s => s.type === 'train') && <div className="legend-item"><span className="line train"></span><span>Train</span></div>}
                                {mapData.segments.some(s => s.type === 'cab' || s.type === 'transfer' || (mapData.stops && mapData.stops.length > 0)) && <div className="legend-item"><span className="line transfer"></span><span>Cab</span></div>}
                                {mapData.segments.length > 1 && <div className="legend-item"><div className="dot stopover legend-stopover-icon"><FaExchangeAlt size={10} /></div><span>Stopover</span></div>}
                            </div>
                        </div>
                    ) : (
                        <div className="map-error"><Info size={40} /><p>{error || 'Unable to load map data. Please try again later.'}</p>{error && <button className="retry-btn" onClick={handleRetry}>Retry</button>}</div>
                    )}
                </div>
            </div>
            <ShareMapModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                routeData={routeData}
                searchMetadata={searchMetadata}
            />
        </div>,
        document.body
    );
};

export default memo(RouteMapModal);
